import { useState } from 'react'
import { CheckCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

interface Field {
  key: string
  type: 'text' | 'number' | 'date' | 'select' | 'boolean' | 'textarea'
  label: string
  placeholder?: string
  required?: boolean
  options?: { value: string; label: string }[]
  hint?: string
}

interface CardData {
  title?: string
  description?: string
  fields?: Field[]
}

interface Props {
  card: CardData
  onSubmit: (values: Record<string, string>) => void
  submitted: boolean
  submittedValues?: Record<string, string>
}

export default function FormCard({ card, onSubmit, submitted, submittedValues }: Props) {
  const [values, setValues] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {}
    card.fields?.forEach(f => { init[f.key] = '' })
    return init
  })
  const [errors, setErrors] = useState<Record<string, string | undefined>>({})

  if (submitted) {
    const display = submittedValues || values
    const labelMap: Record<string, string> = {}
    const valueToLabel: Record<string, Record<string, string>> = {}
    card.fields?.forEach(f => {
      labelMap[f.key] = f.label
      if (f.options) {
        valueToLabel[f.key] = {}
        f.options.forEach(o => { valueToLabel[f.key][o.value] = o.label })
      }
    })
    const displayVal = (k: string, v: string) => {
      if (valueToLabel[k]?.[v]) return valueToLabel[k][v]
      if (String(v) === 'yes') return 'Ja'
      if (String(v) === 'no') return 'Nej'
      return String(v)
    }

    return (
      <Card className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30">
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
            <span className="text-sm font-semibold text-green-700 dark:text-green-400">
              {card.title || 'Information sparad'}
            </span>
          </div>
          <div className="space-y-1.5">
            {Object.entries(display)
              .filter(([, v]) => v && v !== 'false' && v !== 'no')
              .map(([k, v]) => (
                <div key={k} className="flex gap-3 text-sm">
                  <span className="text-muted-foreground min-w-[130px] shrink-0">
                    {labelMap[k] || k.split('.').pop()}
                  </span>
                  <span className="font-medium text-foreground">
                    {displayVal(k, v)}
                  </span>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  const validateField = (field: Field, value: string) => {
    const v = value?.toString().trim()
    if (field.required !== false && !v) return 'Obligatoriskt fält'
    if (!v) return null
    const k = field.key.toLowerCase()
    if (k.includes('email') || k.includes('mail')) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Ange en giltig e-postadress'
    }
    if (k.includes('orgnr') || k.includes('org.nr') || k.includes('organisationsnummer')) {
      if (!/^\d{6}-\d{4}$/.test(v)) return 'Ange i formatet XXXXXX-XXXX'
    }
    if (k.includes('personalid') || k.includes('personnr') || k.includes('personalnumber')) {
      if (!/^\d{8}-\d{4}$/.test(v)) return 'Ange i formatet YYYYMMDD-XXXX'
    }
    if (k.includes('phone') || k.includes('tel') || k.includes('telefon')) {
      if (!/^[\d\s+\-() ]{6,}$/.test(v)) return 'Ange ett giltigt telefonnummer'
    }
    return null
  }

  const validate = () => {
    const errs: Record<string, string> = {}
    card.fields?.forEach(f => {
      const err = validateField(f, values[f.key])
      if (err) errs[f.key] = err
    })
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const set = (key: string, val: string) => setValues(v => ({ ...v, [key]: val }))
  const blur = (field: Field) => {
    const err = validateField(field, values[field.key])
    setErrors(prev => ({ ...prev, [field.key]: err || undefined }))
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        {card.title && <CardTitle className="text-base">{card.title}</CardTitle>}
        {card.description && <CardDescription>{card.description}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-4">
        {card.fields?.map(field => (
          <div key={field.key} className="space-y-1.5">
            <Label htmlFor={field.key} className={cn(errors[field.key] && 'text-destructive')}>
              {field.label}
              {field.required !== false && <span className="text-destructive ml-0.5">*</span>}
            </Label>

            {field.type === 'boolean' ? (
              <div className="flex gap-2">
                {[{ value: 'yes', label: 'Ja' }, { value: 'no', label: 'Nej' }].map(o => (
                  <Button
                    key={o.value}
                    type="button"
                    variant={values[field.key] === o.value ? 'default' : 'outline'}
                    className="flex-1"
                    onClick={() => set(field.key, o.value)}
                  >
                    {o.label}
                  </Button>
                ))}
              </div>

            ) : field.type === 'select' && field.options?.length === 2 ? (
              <div className="flex flex-col gap-2">
                {field.options.map(o => (
                  <Button
                    key={o.value}
                    type="button"
                    variant={values[field.key] === o.value ? 'default' : 'outline'}
                    className="w-full h-auto min-h-9 py-2 whitespace-normal text-left justify-start"
                    onClick={() => set(field.key, o.value)}
                  >
                    {o.label}
                  </Button>
                ))}
              </div>

            ) : field.type === 'select' ? (
              <Select value={values[field.key]} onValueChange={val => set(field.key, val)}>
                <SelectTrigger id={field.key} className={cn(errors[field.key] && 'border-destructive')}>
                  <SelectValue placeholder="Välj..." />
                </SelectTrigger>
                <SelectContent>
                  {field.options?.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

            ) : field.type === 'textarea' ? (
              <Textarea
                id={field.key}
                placeholder={field.placeholder}
                value={values[field.key]}
                onChange={e => set(field.key, e.target.value)}
                onBlur={() => blur(field)}
                className={cn('min-h-[80px]', errors[field.key] && 'border-destructive')}
              />

            ) : (
              <Input
                id={field.key}
                type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                placeholder={field.placeholder}
                value={values[field.key]}
                onChange={e => set(field.key, e.target.value)}
                onBlur={() => blur(field)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && validate() && onSubmit(values)}
                className={cn(errors[field.key] && 'border-destructive')}
              />
            )}

            {field.hint && (
              <p className="text-xs text-muted-foreground">{field.hint}</p>
            )}
            {errors[field.key] && (
              <p className="text-xs text-destructive">{errors[field.key]}</p>
            )}
          </div>
        ))}

        <Button className="w-full" onClick={() => validate() && onSubmit(values)}>
          Fortsätt →
        </Button>
      </CardContent>
    </Card>
  )
}
